import { useState, useEffect, useRef } from 'react';
import { Banner, BannerProps } from '@zoopla/onboarding/banner';
import { isMyHomeRelationship } from '@zoopla/map-relationship';
import { GetUserProfileQuery } from '../../queries/getUserProfile.query.generated';
import { useGetUserProfileQuery } from '../../queries/getUserProfile.hook';
import {
    trackPersonalisedBannerClicked,
    trackPersonalisedBannerShown
} from '../../pages/Homepage.tracking';

type AdditionalData = {
    title?: string;
    content?: string;
    linkPath?: string;
};

const getVariant = (
    data: GetUserProfileQuery
): { variant: BannerProps['variant']; additionalInfo?: AdditionalData } => {
    if (!data?.user?.profile) {
        return {
            variant: 'no_your_situation'
        };
    }

    const userProfile = data?.user?.profile;
    const hasClaimedMyHome = isMyHomeRelationship(
        data?.user?.trackedProperties?.items[0]?.relationship
    );
    const claimedProperty =
        data?.user?.trackedProperties?.items[0]?.property?.address?.fullAddress;
    const claimedPropertyUPRN =
        data?.user?.trackedProperties?.items[0]?.property?.uprn;
    const claimedPropertyEstimate =
        data?.user?.trackedProperties?.items[0]?.property?.saleEstimate
            ?.currentPrice;

    const hasAnsweredBefore = Object.values(userProfile)
        .filter((item) => item !== 'UserProfile')
        .some((situationKey) => !!situationKey);

    // No existing your situation answers
    if (!hasAnsweredBefore) {
        return {
            variant: 'no_your_situation'
        };
    }

    // First time buyer
    if (userProfile?.situationAreYouAFirstTimeBuyer === 'yes') {
        return {
            variant: 'first_time_buyer'
        };
    }

    // Renter
    if (
        userProfile?.situationWhatIsYourCurrentPropertyGoal ===
        'rent_a_property'
    ) {
        return {
            variant: 'renter'
        };
    }

    // Seller
    if (
        userProfile?.situationWhatIsYourCurrentPropertyGoal ===
            'sell_my_property' ||
        userProfile?.situationAreYouSellingAProperty === 'yes'
    ) {
        if (hasClaimedMyHome) {
            const estimatePrice = `£${claimedPropertyEstimate?.toLocaleString(
                'en-GB'
            )}`;
            const content = `Zoopla’s latest estimate for ${claimedProperty} is ${estimatePrice}. Get the most accurate valuation from an agent (or three).`;
            const linkPath = `/agent-matcher?uprn=${claimedPropertyUPRN}`;

            return {
                variant: 'seller',
                additionalInfo: { content, linkPath }
            };
        }

        return {
            variant: 'seller_not_claimed'
        };
    }

    const hasPropertyData =
        !!claimedPropertyEstimate && !!claimedProperty && !!claimedPropertyUPRN;

    // Homeowner
    if (
        userProfile?.situationWhatIsYourCurrentPropertyGoal ===
            'track_the_value_of_my_home' ||
        userProfile?.situationAreYouAFirstTimeBuyer === 'no' ||
        userProfile?.situationAreYouAHomeowner === 'yes'
    ) {
        if (hasClaimedMyHome) {
            // the data we would show is not available, so we show the generic variant
            if (!hasPropertyData) {
                return {
                    variant: 'generic'
                };
            }

            const estimatePrice = `£${claimedPropertyEstimate?.toLocaleString(
                'en-GB'
            )}`;
            const title = `The latest estimate for your home is ${estimatePrice}`;
            const content = `Learn even more about the estimate for ${claimedProperty} by checking how it compares to last month and the rest of the local market.`;
            const linkPath = `/myaccount/my-home/uprn/${claimedPropertyUPRN}/`;

            return {
                variant: 'homeowner',
                additionalInfo: { title, content, linkPath }
            };
        }

        return {
            variant: 'homeowner_not_claimed'
        };
    }

    return {
        variant: 'generic'
    };
};

export const PersonalisedBanner = () => {
    const [personalisedVariant, setPersonalisedVariant] =
        useState<BannerProps['variant']>();
    const [additionalInfo, setAdditionalInfo] = useState<AdditionalData>({});
    const abort = useRef(false);

    useEffect(() => {
        return () => {
            abort.current = true;
        };
    }, []);

    const setVariantAndData = (data: GetUserProfileQuery) => {
        if (abort.current) return;

        const { variant, additionalInfo } = getVariant(data);

        setPersonalisedVariant(variant);

        if (additionalInfo) {
            setAdditionalInfo(additionalInfo);
        }
    };

    useEffect(() => {
        if (personalisedVariant) {
            trackPersonalisedBannerShown(personalisedVariant);
        }
    }, [personalisedVariant]);

    const { loading: isUserprofileLoading } = useGetUserProfileQuery({
        onCompleted: setVariantAndData
    });

    return (
        <Banner
            variant={personalisedVariant}
            onButtonClick={() =>
                trackPersonalisedBannerClicked(personalisedVariant || 'generic')
            }
            title={additionalInfo?.title}
            content={additionalInfo?.content}
            linkPath={additionalInfo?.linkPath}
            loading={isUserprofileLoading || !personalisedVariant}
        />
    );
};
